import React, { useContext, useMemo } from 'react';
import { useIntl } from 'react-intl';
import AutoSizer from 'react-virtualized-auto-sizer';
import {
  ApiFilterOperation,
  Filter,
  ScrollableSource,
  Table,
  TableHandle,
  UserContext,
} from '@eas/common-web';
import { getUserDepartmentIdsList } from '@modules/department/department-api';
import { useColumns } from '@modules/work-order/work-order-columns';
import { WorkOrder } from '@models';
import { ExportTag } from '@enums';
import { useStyles } from '../planner-styles';
import { BulkUnplanSelectedWorkOrders } from './work-order-bulk-unplan';
import { WorkOrderRow } from './work-order-row';
import { useWorkOrderTableStyles } from './work-order-styles';
import { WorkOrderTableCustomToolbar } from './work-order-table-custom-toolbar';
import {
  ClearWorkOrderTableCacheContext,
  useTableWebsocketUtils,
} from './work-order-table-websocket-utils';

const tableId = 'CALENDAR__WORK_ORDER_TABLE';
export const CALENDAR_TABLE_VERSION = 16;

export function WorkOrderTable({
  workOrderTableRef,
  dataSource,
  dragging,
}: {
  workOrderTableRef: React.RefObject<TableHandle<WorkOrder>>;
  dataSource: ScrollableSource<WorkOrder>;
  dragging?: boolean;
}) {
  const columns = useColumns();
  const classes = useStyles();
  const useTableStyles = useWorkOrderTableStyles(dragging);
  const intl = useIntl();

  const {
    registerListener,
    resendAllChangedWorkOrders,
    clearCache,
    AutoClearCacheOnStateChange,
  } = useTableWebsocketUtils();

  const { user } = useContext(UserContext);

  const defaultPreFilters: Filter[] = useMemo(() => {
    const filters: Filter[] = [];
    const departmentList = getUserDepartmentIdsList(user);
    if (departmentList.length > 0) {
      filters.push({
        operation: ApiFilterOperation.OR,
        filters: departmentList.map((departmentId) => ({
          field: 'department.id',
          operation: ApiFilterOperation.EQ,
          value: departmentId,
        })),
      });
    }
    return filters;
  }, [user]);

  return (
    <ClearWorkOrderTableCacheContext.Provider
      value={{ clearCache, registerListener }}
    >
      <div className={classes.bottomWindow}>
        <AutoSizer disableWidth={true} style={{ height: '100%' }}>
          {({ height }) => {
            return (
              <Table
                ref={workOrderTableRef}
                version={CALENDAR_TABLE_VERSION}
                tableId={tableId}
                tableName={intl.formatMessage({
                  id: 'CALENDAR__WORK_ORDER_TABLE',
                  defaultMessage: 'Pracovné príkazy',
                })}
                ToolbarComponent={WorkOrderTableCustomToolbar}
                toolbarProps={{
                  before: (
                    <>
                      <AutoClearCacheOnStateChange />
                      <BulkUnplanSelectedWorkOrders clearCache={clearCache} />
                    </>
                  ),
                }}
                defaultPreFilters={defaultPreFilters}
                columns={columns}
                source={dataSource}
                height={height}
                useStyles={useTableStyles}
                showNamedSettingsButton={true}
                showBulkActionButton={false}
                showSelectBox={true}
                showRefreshButton={false}
                showSearchbar={false}
                showReportButton={true}
                RowComponent={WorkOrderRow}
                showResetSortsButton={true}
                afterItemsRendered={resendAllChangedWorkOrders}
                reportTag={ExportTag.WORK_ORDERS}
              />
            );
          }}
        </AutoSizer>
      </div>
    </ClearWorkOrderTableCacheContext.Provider>
  );
}
